import { useBackgroundQuery } from "@apollo/client";
import { QueryRef } from "@apollo/client/react/types/types";
import {
  WasteSubmissionsSummaryQuery,
  WasteSubmissionsByStateQuery,
  WasteSubmissionsByGeneratorStatusQuery,
  SubmissionStatus,
  RegulatoryStatusOption,
  WasteSubmissionsSummaryFilter,
} from "generated-graphql/graphql";
import { gql } from "generated-graphql";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import {
  ReactNode,
  createContext,
  useContext,
  useDeferredValue,
  useMemo,
} from "react";
import invariant from "tiny-invariant";

// Default statuses (exclude FILING_COMPLETE and NOT_REPORTING)
// This matches the table's default submissionStatus filter
const DEFAULT_STATUSES = [
  SubmissionStatus.NotStarted,
  SubmissionStatus.InProgress,
  SubmissionStatus.EncampInitialReview,
  SubmissionStatus.InReview,
  SubmissionStatus.ProcessingSubmission,
];

// Filter type that matches what the parent component computes
// This extends WasteSubmissionsSummaryFilter with additional fields the parent tracks
export type WasteSubmissionsOverviewFilter = {
  dueDateStart?: string;
  dueDateEnd?: string;
  states?: string[];
  statuses?: SubmissionStatus[];
  generatorStatuses?: RegulatoryStatusOption[];
  facilityTagNames?: string[];
  readyForActionEmail?: string;
};

// Queries moved here to be centralized in the context
// Charts use the same default filters as the table (current year, non-completed statuses)
// to ensure counts are consistent between the overview charts and the data grid
export const WASTE_SUBMISSIONS_SUMMARY_QUERY = gql(`
  query WasteSubmissionsSummary($tenantId: ID!, $filter: WasteSubmissionsSummaryFilter) {
    wasteSubmissionsSummary(tenantId: $tenantId, filter: $filter) {
      byStatus {
        status
        count
      }
      total
    }
  }
`);

export const WASTE_SUBMISSIONS_BY_STATE_QUERY = gql(`
  query WasteSubmissionsByState($tenantId: ID!, $filter: WasteSubmissionsSummaryFilter) {
    wasteSubmissionsSummary(tenantId: $tenantId, filter: $filter) {
      byState {
        state
        count
      }
      total
    }
  }
`);

export const WASTE_SUBMISSIONS_BY_GENERATOR_STATUS_QUERY = gql(`
  query WasteSubmissionsByGeneratorStatus($tenantId: ID!, $filter: WasteSubmissionsSummaryFilter) {
    wasteSubmissionsSummary(tenantId: $tenantId, filter: $filter) {
      byGeneratorStatus {
        generatorStatus
        count
      }
      total
    }
  }
`);

type WasteSubmissionsOverviewContextType = {
  summaryQuery: QueryRef<WasteSubmissionsSummaryQuery>;
  byStateQuery: QueryRef<WasteSubmissionsByStateQuery>;
  byGeneratorStatusQuery: QueryRef<WasteSubmissionsByGeneratorStatusQuery>;
};

const WasteSubmissionsOverviewContext = createContext<
  WasteSubmissionsOverviewContextType | undefined
>(undefined);

export function useWasteSubmissionsOverviewContext() {
  const context = useContext(WasteSubmissionsOverviewContext);
  if (!context) {
    throw new Error(
      "useWasteSubmissionsOverviewContext must be used within a WasteSubmissionsOverviewProvider"
    );
  }
  return context;
}

type WasteSubmissionsOverviewProviderProps = {
  children: ReactNode;
  /**
   * Filter to apply to all overview queries.
   * When provided, overrides the default filter.
   * The parent component is responsible for computing the complete filter
   * including any defaults (statuses, date range, etc).
   */
  filter?: WasteSubmissionsOverviewFilter;
};

export function WasteSubmissionsOverviewProvider({
  children,
  filter: externalFilter,
}: WasteSubmissionsOverviewProviderProps) {
  const { tenantId } = useTenant();
  invariant(tenantId, "tenantId is required");

  // Build the effective filter for queries
  // When external filter is provided, use it directly (parent handles defaults)
  // When no external filter, use internal defaults for backwards compatibility
  const effectiveFilter = useMemo((): WasteSubmissionsSummaryFilter | undefined => {
    if (externalFilter) {
      // External filter provided - convert to GraphQL filter type
      const graphqlFilter: WasteSubmissionsSummaryFilter = {};
      
      if (externalFilter.dueDateStart) {
        graphqlFilter.dueDateStart = externalFilter.dueDateStart;
      }
      if (externalFilter.dueDateEnd) {
        graphqlFilter.dueDateEnd = externalFilter.dueDateEnd;
      }
      if (externalFilter.states && externalFilter.states.length > 0) {
        graphqlFilter.states = externalFilter.states;
      }
      if (externalFilter.statuses && externalFilter.statuses.length > 0) {
        graphqlFilter.statuses = externalFilter.statuses;
      }
      if (externalFilter.generatorStatuses && externalFilter.generatorStatuses.length > 0) {
        graphqlFilter.generatorStatuses = externalFilter.generatorStatuses;
      }
      if (externalFilter.facilityTagNames && externalFilter.facilityTagNames.length > 0) {
        graphqlFilter.facilityTagNames = externalFilter.facilityTagNames;
      }
      if (externalFilter.readyForActionEmail) {
        graphqlFilter.readyForActionEmail = externalFilter.readyForActionEmail;
      }
      
      return Object.keys(graphqlFilter).length > 0 ? graphqlFilter : undefined;
    }
    
    // No external filter - use internal defaults
    // This matches the table's default filtering behavior:
    // - Only show non-completed statuses (exclude FILING_COMPLETE, NOT_REPORTING)
    // - Only show current quarter's submissions (updated from year to quarter)
    const now = DateTime.now();
    return {
      statuses: DEFAULT_STATUSES,
      dueDateStart: now.startOf("quarter").toISO(),
      dueDateEnd: now.endOf("quarter").toISO(),
    };
  }, [externalFilter]);

  // Use deferred value to prevent re-suspension when filter changes
  // This keeps showing the old data while new data loads in the background
  // Similar to how WasteOverviewContext uses useTransition for filter updates
  const deferredFilter = useDeferredValue(effectiveFilter);

  const [summaryQuery] = useBackgroundQuery(WASTE_SUBMISSIONS_SUMMARY_QUERY, {
    variables: {
      tenantId,
      filter: deferredFilter,
    },
  });

  const [byStateQuery] = useBackgroundQuery(WASTE_SUBMISSIONS_BY_STATE_QUERY, {
    variables: {
      tenantId,
      filter: deferredFilter,
    },
  });

  const [byGeneratorStatusQuery] = useBackgroundQuery(
    WASTE_SUBMISSIONS_BY_GENERATOR_STATUS_QUERY,
    {
      variables: {
        tenantId,
        filter: deferredFilter,
      },
    }
  );

  const value = useMemo(
    () => ({
      summaryQuery,
      byStateQuery,
      byGeneratorStatusQuery,
    }),
    [summaryQuery, byStateQuery, byGeneratorStatusQuery]
  );

  return (
    <WasteSubmissionsOverviewContext.Provider value={value}>
      {children}
    </WasteSubmissionsOverviewContext.Provider>
  );
}
